import { useState, useMemo } from "react";
import { Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import MQTTService from "@/services/MQTTService";
import { useAutomations } from "@/context/AutomationsProvider";

type AutomateProcessButtonProps = {
  workspaceId?: string;
  automationServerId?: string;
};

export default function AutomateProcessButton(props: AutomateProcessButtonProps) {
  const { workspaceId, automationServerId } = props;
  const { workspaces } = useAutomations();
  const navigate = useNavigate();
  const workspaceList = Object.values(workspaces);
  const [open, setOpen] = useState(false);
  const [processName, setProcessName] = useState("");
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>("");
  const [selectedAutomationServerId, setSelectedAutomationServerId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");

  const needsWorkspaceSelection = !workspaceId || !automationServerId;
  const finalWorkspaceId = workspaceId || selectedWorkspaceId;
  const finalAutomationServerId = automationServerId || selectedAutomationServerId;

  // Filter workspaces based on search query
  const filteredWorkspaces = useMemo(() => {
    if (!searchQuery.trim()) {
      return workspaceList;
    }
    const query = searchQuery.toLowerCase();
    return workspaceList.filter((workspace) =>
      workspace.name.toLowerCase().includes(query)
    );
  }, [workspaceList, searchQuery]);

  const handleWorkspaceChange = (value: string) => {
    const [serverId, wsId] = value.split("|");
    setSelectedAutomationServerId(serverId);
    setSelectedWorkspaceId(wsId);
  };

  const handleContinue = async () => {
    if (processName.trim() && finalWorkspaceId && finalAutomationServerId) {
      const processId = MQTTService.getInstance().createProcess(processName.trim(), finalWorkspaceId, finalAutomationServerId);
      setProcessName("");
      setSelectedWorkspaceId("");
      setSelectedAutomationServerId("");
      setOpen(false);
      
      // Wait for the process to appear in the state before navigating
      const maxWaitTime = 10000; // 10 seconds max
      const pollInterval = 200; // Check every 200ms
      const startTime = Date.now();
      
      const waitForProcess = async (): Promise<boolean> => {
        while (Date.now() - startTime < maxWaitTime) {
          const state = MQTTService.getInstance().getState();
          if (state.processes && state.processes[processId]) {
            return true;
          }
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
        return false;
      };
      
      const processFound = await waitForProcess();
      if (processFound) {
        navigate(`/workspaces/${finalWorkspaceId}/processes/${processId}`);
      } else {
        // Navigate anyway after timeout - process may still be loading
        console.warn(`Process ${processId} not found in state after ${maxWaitTime}ms, navigating anyway`);
        navigate(`/workspaces/${finalWorkspaceId}/processes/${processId}`);
      }
    }
  };

  const handleCancel = () => {
    setProcessName("");
    setSelectedWorkspaceId("");
    setSelectedAutomationServerId("");
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Reset state when dialog closes
      setProcessName("");
      setSelectedWorkspaceId("");
      setSelectedAutomationServerId("");
      setSearchQuery("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus size={16} />
          Automate process
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Name the process</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <Input
              placeholder="Process name..."
              value={processName}
              onChange={(e) => setProcessName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && processName.trim() && (!needsWorkspaceSelection || (selectedWorkspaceId && selectedAutomationServerId))) {
                  handleContinue();
                }
              }}
              autoFocus
            />
          </div>
          {needsWorkspaceSelection && (
            <div className="space-y-3">
              <Label>
                Choose a workspace for this process <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="search"
                  placeholder="Search workspaces..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <RadioGroup
                value={selectedWorkspaceId && selectedAutomationServerId ? `${selectedAutomationServerId}|${selectedWorkspaceId}` : ""}
                onValueChange={handleWorkspaceChange}
              >
                <div className="space-y-2 min-h-[200px] max-h-[60vh] overflow-y-auto">
                  {filteredWorkspaces.length === 0 ? (
                    <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                      No workspaces found
                    </div>
                  ) : (
                    filteredWorkspaces.map((workspace) => (
                      <div key={`${workspace.automation_server}-${workspace.id}`} className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={`${workspace.automation_server}|${workspace.id}`}
                          id={`workspace-${workspace.id}`}
                        />
                        <Label
                          htmlFor={`workspace-${workspace.id}`}
                          className="font-normal cursor-pointer"
                        >
                          {workspace.name}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              </RadioGroup>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!processName.trim() || (needsWorkspaceSelection && (!selectedWorkspaceId || !selectedAutomationServerId))}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
